import { useState } from "react"
import type { Launch } from "@models/launch.model"
import { LaunchService } from "@services/launches/LaunchService"
import type { CampaignDatesPreview } from "@services/launches/commands/cmdCampaignDates"
import { CampaignDatesConfirm } from "./CampaignDatesConfirm"

/**
 * The campaign's start and end, set here and nowhere else (MPN-2040).
 *
 * They were only editable as a row on the Schedule page, which is why Ops describe them as
 * unofficial — and why Budget and Schedule now show them read-only with a link back here.
 *
 * Start and Went live are two facts, not one. The ticket says the start "becomes the actual
 * go-live date and locks" at first publish, but 21 of the 57 published campaigns on the
 * production dump have a schedule start that differs from `go_live_date`, three of them by
 * more than 190 days — a long-running campaign re-cut for a new term, where go-live records
 * the first publish ever. Collapsing them would tell Ops that campaign started in January.
 * So "locks" is read as "stops being an estimate": the start keeps its own value and stays
 * editable, and Went live sits beside it as the system's record.
 */
export function CampaignDatesCard({
  campaign,
  onChanged,
}: {
  campaign: Launch
  onChanged: (campaign: Launch) => void
}) {
  const [editing, setEditing] = useState(false)
  const [start, setStart] = useState(campaign.campaign_start ?? "")
  const [end, setEnd] = useState(campaign.campaign_end ?? "")
  const [preview, setPreview] = useState<CampaignDatesPreview | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const service = new LaunchService()
  const saved = { start: campaign.campaign_start ?? "", end: campaign.campaign_end ?? "" }
  const moved = start !== saved.start || end !== saved.end

  const beginEdit = () => {
    setStart(saved.start)
    setEnd(saved.end)
    setError(null)
    setEditing(true)
  }

  const askToSave = async () => {
    if (!start || !end) {
      setError("A campaign needs both a start and an end date.")
      return
    }
    setBusy(true)
    setError(null)
    try {
      // The server works out what moves. Asking it rather than guessing here is what makes
      // the confirmation and the save the same calculation.
      setPreview(await service.previewCampaignDates(campaign.id, start, end))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const commit = async (followAdGroupIds: number[], note: string) => {
    setBusy(true)
    setError(null)
    try {
      await service.setCampaignDates(campaign.id, start, end, followAdGroupIds, note)
      onChanged({ ...campaign, campaign_start: start, campaign_end: end })
      setPreview(null)
      setEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setPreview(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="campaign-label campaign-dates__heading">
        Campaign Dates
        {!editing && (
          <button type="button" className="campaign-dates__edit" onClick={beginEdit}>
            Edit
          </button>
        )}
      </div>

      <div className="campaign-card campaign-dates">
        <Field label={campaign.go_live_date ? "Start" : "Start (estimated)"}>
          {editing ? (
            <input
              type="date"
              className="form-control form-control-sm"
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          ) : (
            <Value day={saved.start} />
          )}
        </Field>

        <Field label="End">
          {editing ? (
            <input
              type="date"
              className="form-control form-control-sm"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          ) : (
            <Value day={saved.end} />
          )}
        </Field>

        <Field label="Duration">
          {/* Follows the fields above while editing. Showing the saved span beside two
              changed dates states a length that matches neither. */}
          <span className="campaign-dates__value">
            {editing ? duration(start, end) : duration(saved.start, saved.end)}
          </span>
        </Field>

        {/* The system's own record of when this campaign first reached a network. Read-only
            everywhere: nothing can make a publish have happened on a different day. */}
        <Field label="Went live">
          <Value day={campaign.go_live_date ?? ""} empty="Not yet published" />
        </Field>
      </div>

      {error && <div className="alert alert-danger py-2 small mt-2">{error}</div>}

      {editing && (
        <div className="campaign-dates__actions">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setEditing(false)}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={askToSave}
            disabled={busy || !moved}
          >
            {busy ? "Checking…" : "Save"}
          </button>
        </div>
      )}

      {preview && (
        <CampaignDatesConfirm
          preview={preview}
          busy={busy}
          onCancel={() => setPreview(null)}
          onConfirm={commit}
        />
      )}
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="campaign-label">{label}</div>
      {children}
    </div>
  )
}

function Value({ day, empty = "Not set" }: { day: string; empty?: string }) {
  if (!day) return <span className="campaign-dates__value campaign-dates__value--none">{empty}</span>
  return <span className="campaign-dates__value">{fmt(day)}</span>
}

/** Days remaining beside the span, because "90 days" alone does not say how many are left. */
function duration(start: string, end: string): string {
  if (!start || !end) return "—"
  const day = 86_400_000
  const total = Math.round((Date.parse(end) - Date.parse(start)) / day) + 1
  const today = new Date().toISOString().slice(0, 10)
  const left = Math.round((Date.parse(end) - Date.parse(today)) / day) + 1
  if (left <= 0) return `${total} days · ended`
  if (left >= total) return `${total} days`
  return `${total} days · ${left} left`
}

function fmt(day: string): string {
  return new Date(`${day}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}
